/* CASH REPORT — the three reports as ONE module (Max, 2026-10-15: "Gộp report của Cash Report · Weekly,
   Cash Report · 13 Weeks Cash Plan (since 2026-10-25 the Monthly Cash Plan), Cash Report · Month-End Reconcile thành 1 module thui").

   A tab each, the way Submit Cash Report has a tab per template. The tab rides in the URL
   (?tab=weekly|plan|monthend) beside the tab's own period; switching tab drops the period, since a
   week and a month are different things. The old addresses — /cash/weekly, /cash/plan,
   /cash/month-end, which every notice mailed before today carries — land on their tab with the rest
   of their query (CashReportLegacy).

   Each tab is the screen it was, without the page around it (43/44/45). What they show is the
   Reviewer-agreed figures only — the server's `inReport` (Max, same day: "data trong report chỉ hiện
   khi reviewer approve nhé"); a report still with its Preparer or its Reviewer is a row that says
   where it is. */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useSearchParams } from 'react-router-dom';
import { Compass } from 'lucide-react';
import Header from './Header';
import TabIcon from './TabIcon';
import TourGuide from './TourGuide';
import { ManualButton, MANUAL_STEP } from './ModuleHelp';
import CashWeekly from './43_CashWeekly';
import CashPlan from './44_CashPlan';
import CashMonthEnd from './45_CashMonthEnd';
import { WholeNumbers } from './cashUi';
import './SPHub.css';
import './ProjectApproval.css';
import './CashSetup.css';
import './CashWeekly.css';

const TABS = [
  { key: 'weekly', nav: 'nav.cashWeekly', Body: CashWeekly },
  { key: 'plan', nav: 'nav.cashPlan', Body: CashPlan },
  { key: 'monthend', nav: 'nav.cashMonthEnd', Body: CashMonthEnd },
];

/* THE GUIDE — Max, 2026-10-16: "với phần cash report thêm tính năng guide luôn". The same TourGuide as
   Submit and Approval, with ONE TOUR PER TAB: the three reports are read differently (a week's
   entities and accounts, a plan's thirteen weeks and its two filters, a month's reconcile), and one
   tour pointing at controls two tabs away would mostly be skipped steps. A step whose control is not
   on the page is skipped, as everywhere else. It opens by itself once, on whichever tab the reader
   lands on, as soon as that tab has drawn its period bar (its data is in). */
const COMMON_HEAD = [
  { selector: '[data-tour="cr-tabs"]', title: 'tour.crTabsT', body: 'tour.crTabsB' },
  { selector: '.cw-page .cw-weekbar', title: 'tour.crPeriodT', body: 'tour.crPeriodB' },
];
const COMMON_TAIL = [
  { selector: '.cw-page .cw-waiting', title: 'tour.crWaitingT', body: 'tour.crWaitingB' },
  { selector: '.cw-testas', title: 'tour.ciTestAsT', body: 'tour.ciTestAsB' },
];
const TOURS = {
  weekly: [
    ...COMMON_HEAD,
    { selector: '.cw-page .cw-charts', title: 'tour.crChartsT', body: 'tour.crChartsWB' },
    { selector: '[data-tour="cr-week"] .cw-weekfixed', title: 'tour.crWeekT', body: 'tour.crWeekB' },
    { selector: '[data-tour="cr-week"] .cw-tophead-picks', title: 'tour.crExpandT', body: 'tour.crExpandB' },
    { selector: '[data-tour="cr-top3"]', title: 'tour.crTopT', body: 'tour.crTopB' },
    ...COMMON_TAIL,
  ],
  plan: [
    ...COMMON_HEAD,
    { selector: '[data-tour="mp-tabs"]', title: 'xCashMp.tourTabsT', body: 'xCashMp.tourTabsB' },
    { selector: '[data-tour="mp-group"]', title: 'xCashMp.tourGroupT', body: 'xCashMp.tourGroupB' },
    { selector: '[data-tour="mp-charts"]', title: 'xCashMp.tourChartsT', body: 'xCashMp.tourChartsB' },
    ...COMMON_TAIL,
  ],
  monthend: [
    ...COMMON_HEAD,
    { selector: '.cw-page .cw-charts', title: 'tour.crChartsT', body: 'tour.crChartsMB' },
    { selector: '.cw-monthend', title: 'tour.crMonthT', body: 'tour.crMonthB' },
    ...COMMON_TAIL,
  ],
};
const TOUR_SEEN = 'portalCashReportTourSeen';

export default function CashReport() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const tab = TABS.find((x) => x.key === params.get('tab')) || TABS[0];
  // Pressing the tab already lit changes nothing, or it would throw away the period somebody came in on.
  const pick = (key) => { if (key !== tab.key) setParams(new URLSearchParams({ tab: key }), { replace: true }); };
  const { Body } = tab;

  /* Once, unasked — written when it ENDS (Done or Skip), the Sample Tracker's rule. It waits for the
     tab's period bar, which is drawn only once the tab's data has arrived; a tour started over a
     spinner would skip every step. */
  const [tourOpen, setTourOpen] = useState(false);
  useEffect(() => {
    let seen = true;
    try { seen = !!localStorage.getItem(TOUR_SEEN); } catch { seen = true; }
    if (seen) return undefined;
    let tries = 0;
    const id = window.setInterval(() => {
      tries += 1;
      if (document.querySelector('.cw-page .cw-weekbar')) { window.clearInterval(id); window.setTimeout(() => setTourOpen(true), 400); }
      else if (tries > 40) window.clearInterval(id);
    }, 300);
    return () => window.clearInterval(id);
  }, []);
  const closeTour = () => {
    setTourOpen(false);
    try { localStorage.setItem(TOUR_SEEN, '1'); } catch { /* nothing to remember it in */ }
  };

  return (
    <div className="sp-page">
      <Header />
      <div className="sp-main cs-page cw-page">
        {/* The title alone — no description under it (Max, 2026-10-15: "bỏ phần description này trên các
            tab report nhé"); what each tab is, its tab says. */}
        <div className="ci-titlerow">
          <div className="pa-title cw-reporttitle">
            <h2>{t('cashr.title')}</h2>
          </div>
          <div className="mh-bar">
            <button type="button" className="pa-btn ci-guide" title={t('st.tourReopen')} onClick={() => setTourOpen(true)}>
              <Compass size={14} /> {t('st.tourButton')}
            </button>
            <ManualButton manual="cash-roles" />
          </div>
        </div>
        <div className="pa-tabs cw-tpltabs cw-reporttabs view-tabs" role="tablist" data-tour="cr-tabs">
          {TABS.map((x) => (
            <button key={x.key} type="button" role="tab" aria-selected={x.key === tab.key}
              className={`pa-tab${x.key === tab.key ? ' active' : ''}`} onClick={() => pick(x.key)}>
              <TabIcon k={x.key} />{t(x.nav)}
            </button>
          ))}
        </div>
        <WholeNumbers.Provider value>
          <Body key={tab.key} />
        </WholeNumbers.Provider>
      </div>
      {/* Keyed by the tab: a tab switch while the tour is open starts that tab's own tour. */}
      <TourGuide key={tab.key} steps={[...TOURS[tab.key], MANUAL_STEP]} open={tourOpen} onClose={closeTour} onFinish={closeTour} />
    </div>
  );
}

/* /cash/weekly, /cash/plan, /cash/month-end — the addresses of the three screens before they became
   tabs. Kept as doors: a notice mailed last week opens the same report it always did. */
export function CashReportLegacy({ tab }) {
  const [params] = useSearchParams();
  const q = new URLSearchParams(params);
  q.set('tab', tab);
  return <Navigate to={`/cash/report?${q}`} replace />;
}
